import React from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { LinkedLogo, NamedLink } from '../../components';

import css from './DriveloFooter.module.css';

const cms = pageId => ({ name: 'CMSPage', params: { pageId } });

/**
 * Footer link columns, in display order. Each link is a named route; `href` is for mail links.
 * Translations: `DriveloFooter.{column}Title` and `DriveloFooter.link.{key}`.
 */
export const FOOTER_COLUMNS = [
  {
    key: 'learners',
    links: [
      { key: 'findInstructor', name: 'SearchPage' },
      { key: 'howItWorks', ...cms('how-it-works') },
      { key: 'pricing', ...cms('pricing') },
      { key: 'firstLesson', ...cms('first-lesson-guide') },
      { key: 'testPrep', ...cms('driving-test-prep') },
      { key: 'overseas', ...cms('overseas-licence-conversion') },
    ],
  },
  {
    key: 'instructors',
    links: [
      { key: 'becomeInstructor', ...cms('become-an-instructor') },
      { key: 'instructorAgreement', ...cms('instructor-agreement') },
      { key: 'codeOfConduct', ...cms('instructor-code-of-conduct') },
    ],
  },
  {
    key: 'drivelo',
    links: [
      { key: 'ourStory', ...cms('our-story') },
      { key: 'faq', ...cms('faq') },
      { key: 'roadmap', ...cms('roadmap') },
      { key: 'contact', href: 'mailto:hello@drivelo.com.au' },
    ],
  },
];

const LEGAL_LINKS = [
  { key: 'terms', ...cms('terms-of-service') },
  { key: 'privacy', ...cms('privacy-policy') },
  { key: 'cancellation', ...cms('cancellation-policy') },
  { key: 'policies', ...cms('policies') },
];

const FooterLink = props => {
  const { link, className } = props;
  const label = <FormattedMessage id={`DriveloFooter.link.${link.key}`} />;
  return link.href ? (
    <a href={link.href} className={className}>
      {label}
    </a>
  ) : (
    <NamedLink name={link.name} params={link.params} className={className}>
      {label}
    </NamedLink>
  );
};

/**
 * Drivelo's site footer: logo and tagline, three titled link columns, and a bottom row with
 * the company line and legal links. Built in code instead of from the Console footer asset, so
 * it no longer loads the page builder on every page.
 *
 * @component
 * @returns {JSX.Element}
 */
const DriveloFooter = () => {
  const intl = useIntl();
  const year = new Date().getFullYear();

  return (
    <footer className={css.root}>
      <div className={css.content}>
        <div className={css.brand}>
          <LinkedLogo
            rootClassName={css.logoLink}
            logoClassName={css.logo}
            logoImageClassName={css.logoImage}
            layout="desktop"
            alt={intl.formatMessage({ id: 'DriveloFooter.logoAlt' })}
          />
          <p className={css.tagline}>
            <FormattedMessage id="DriveloFooter.tagline" />
          </p>
        </div>

        <nav
          className={css.columns}
          aria-label={intl.formatMessage({ id: 'DriveloFooter.navLabel' })}
        >
          {FOOTER_COLUMNS.map(column => (
            <div key={column.key} className={css.column}>
              <h2 className={css.columnTitle}>
                <FormattedMessage id={`DriveloFooter.${column.key}Title`} />
              </h2>
              <ul className={css.linkList}>
                {column.links.map(link => (
                  <li key={link.key}>
                    <FooterLink link={link} className={css.link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </div>

      <div className={css.bottom}>
        <div className={css.bottomContent}>
          <p className={css.company}>
            <FormattedMessage id="DriveloFooter.company" values={{ year }} />
          </p>
          <ul className={css.legalList}>
            {LEGAL_LINKS.map(link => (
              <li key={link.key}>
                <FooterLink link={link} className={css.legalLink} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default DriveloFooter;
